import React from 'react';
import { combinedOdds, entryResult, formatDate } from '../lib/tips';

const BADGE = {
  WIN: 'bg-win/10 text-win border-win/20',
  LOST: 'bg-loss/10 text-loss border-loss/20',
  PENDING: 'bg-ice text-royal border-royal/20',
  NONE: 'bg-mist text-slate-500 border-line'
};

export default function EntryCard({ entry, compact = false }) {
  const result = entryResult(entry);
  const combined = combinedOdds(entry.matches);
  const isDouble = entry.matches.length > 1;
  const id = `${entry.tipster_id}-${entry.date.replace(/\./g, '-')}`;

  return (
    <article className="card-lift rounded-3xl border border-line bg-white overflow-hidden" data-testid={`entry-${id}`}>
      <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-line bg-mist/60">
        <div>
          <div className="font-mono text-sm font-bold text-navy">{entry.date}</div>
          {!compact && <div className="text-[11px] text-slate-500 mt-0.5">{formatDate(entry.date)}</div>}
        </div>
        <div className="flex items-center gap-2">
          {compact && isDouble && result !== 'NONE' && (
            <span className="font-mono text-sm font-bold text-navy" data-testid={`entry-combined-${id}`}>×{combined?.toFixed(2)}</span>
          )}
          <span className={`inline-flex items-center px-3 py-1 rounded-full border text-[11px] font-mono font-bold tracking-wider ${BADGE[result]}`} data-testid={`entry-result-${id}`}>
            {result === 'NONE' ? 'NO PICK' : result === 'PENDING' ? 'PENDING' : isDouble ? `DOUBLE ${result}` : result}
          </span>
        </div>
      </div>

      {result === 'NONE' ? (
        <div className="px-5 py-5 text-sm text-slate-500 italic">{entry.note || 'No selection released on this day.'}</div>
      ) : (
        <div className={`grid ${isDouble && !compact ? 'md:grid-cols-[1fr_140px]' : ''}`}>
          <div className="divide-y divide-line">
            {entry.matches.map((m, i) => (
              <div key={i} className="px-5 py-4 flex items-center gap-4" data-testid={`entry-match-${id}-${i}`}>
                {isDouble && <div className="font-mono text-xs text-slate-400 w-5">0{i + 1}</div>}
                <div className="flex-1 min-w-0">
                  {m.league && <div className="text-[11px] text-slate-500 mb-0.5 truncate">{m.league}</div>}
                  <div className="font-display font-semibold text-navy text-[15px] truncate">{m.home} <span className="text-slate-400 font-normal">vs</span> {m.away}</div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                    <span className="px-2 py-0.5 rounded-md bg-ice text-royal font-semibold">{m.pick}</span>
                    {m.ft && <span className="text-slate-500">FT <span className="font-mono font-semibold text-navy">{m.ft}</span></span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-lg font-bold text-navy">{Number(m.odd).toFixed(2)}</div>
                  <div className={`text-[11px] font-mono font-bold ${m.result === 'WIN' ? 'text-win' : m.result === 'LOST' ? 'text-loss' : 'text-royal'}`}>{m.result}</div>
                </div>
              </div>
            ))}
          </div>
          {isDouble && !compact && (
            <div className={`flex md:flex-col items-center justify-between md:justify-center gap-1 px-5 md:px-3 py-3 md:py-0 border-t md:border-t-0 md:border-l border-line ${result === 'WIN' ? 'bg-win/[0.06]' : result === 'LOST' ? 'bg-loss/[0.05]' : 'bg-mist'}`}>
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Combined</div>
              <div className="font-mono text-2xl font-bold text-navy leading-none" data-testid={`entry-combined-${id}`}>{combined?.toFixed(2)}</div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}
